import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TrendingUp, TrendingDown, BarChart, Globe, Bitcoin, ChevronDown, Gauge, Sparkles, CalendarDays, Clock3, Hash, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useImportedTradesRecords } from '../../hooks/useImportedTrades';
import { useAssetClassVisibility } from '../../lib/assetClassVisibility';
import { normalizeTimestampToMs } from '../../lib/time/normalizeTimestampToMs';
import { useDisplayTimezone } from '../app/DisplayTimezoneProvider';
import { getNewYorkDateKey } from '../../lib/market/usEquityMarketCalendar';
import {
  getInvestmentPositionHistory,
  type InvestmentDailyBar,
  type InvestmentHistoryResponse,
} from '../../services/investmentHistoryApi';

// --- TYPES ---

type TimeframeKey = '1M' | '3M' | '6M' | '1Y' | '3Y' | '5Y' | 'MAX';
type AssetClass = 'STOCK' | 'FUTURES' | 'CRYPTO' | 'FOREX';
type Direction = 'LONG' | 'SHORT';
type ThemeKey = 'blue' | 'yellow' | 'purple' | 'emerald';

type InvestmentAsset = {
  id: string;
  ticker: string;
  name: string;
  assetClass: AssetClass;
  type: Direction;
  shares: number;
  unitName: string;
  entryPrice: number;
  currentPrice: number;
  currentMarketPrice?: number | null;
  currentMarketPriceLoading?: boolean;
  currentMarketPriceFailed?: boolean;
  gain: number;
  returnPct: number;
  executed: string;
  entryTimestamp?: number | null;
  exitTimestamp?: number | null;
  theme: ThemeKey;
  isProfitable: boolean;
  isClosed?: boolean;
  liveQuotePrice?: number | null;
  hasLiveQuote?: boolean;
  exitPrice?: number;
  tags?: string[];
  marketHistory?: InvestmentHistoryResponse | null;
  marketHistoryLoading?: boolean;
  marketHistoryError?: string | null;
  fetchFailed?: boolean;
  liveAsOf?: string | null;
  liveQuality?: string | null;
  supportsLiveFetch?: boolean;
};

type LongTermInvestmentsWidgetProps = {
  trades?: InvestmentAsset[];
  page?: number;
  setPage?: (value: number | ((p: number) => number)) => void;
  placementHeight?: number;
  onPageCountChange?: (pageCount: number) => void;
  isDark?: boolean;
};

// --- CONSTANTS ---

const TIMEFRAMES: TimeframeKey[] = ['1M', '3M', '6M', '1Y', '3Y', '5Y', 'MAX'];
const RANGE_DAYS: Record<Exclude<TimeframeKey, 'MAX'>, number> = {
  '1M': 31,
  '3M': 93,
  '6M': 186,
  '1Y': 366,
  '3Y': 1096,
  '5Y': 1827,
};

const filterDailyBarsForRange = (bars: InvestmentDailyBar[], range: TimeframeKey) => {
  if (range === 'MAX' || bars.length < 2) return bars;
  const lastTimestamp = bars[bars.length - 1]?.t;
  const cutoff = lastTimestamp - RANGE_DAYS[range] * 24 * 60 * 60;
  const filtered = bars.filter((bar) => bar.t >= cutoff);
  return filtered.length ? filtered : bars.slice(-1);
};

const themes: Record<ThemeKey, { primary: string; bg: string; text: string; border: string; cardBorder: string }> = {
  blue: { primary: '#0ea5e9', bg: 'bg-[#0ea5e9]', text: 'text-[#0ea5e9]', border: 'border-[#0ea5e9]/40', cardBorder: 'border-[#0ea5e9]/30' },
  yellow: { primary: '#f59e0b', bg: 'bg-[#f59e0b]', text: 'text-[#f59e0b]', border: 'border-[#f59e0b]/40', cardBorder: 'border-[#f59e0b]/30' },
  purple: { primary: '#a855f7', bg: 'bg-[#a855f7]', text: 'text-[#a855f7]', border: 'border-[#a855f7]/40', cardBorder: 'border-[#a855f7]/30' },
  emerald: { primary: '#10b981', bg: 'bg-[#10b981]', text: 'text-[#10b981]', border: 'border-[#10b981]/40', cardBorder: 'border-[#10b981]/30' }
};

const getWidgetUi = (isDark: boolean) => ({
  shell: isDark ? '#111318' : '#f8fafc',
  panel: isDark ? '#14151a' : '#ffffff',
  panelSoft: isDark ? '#191a20' : '#f3f6fb',
  inset: isDark ? '#16181d' : '#eef2f7',
  border: isDark ? 'border-[#22242b]' : 'border-slate-200',
  borderSoft: isDark ? 'border-zinc-800' : 'border-slate-200',
  borderMuted: isDark ? 'border-gray-700/60' : 'border-slate-200',
  text: isDark ? 'text-white' : 'text-slate-900',
  muted: isDark ? 'text-gray-400' : 'text-slate-500',
  subtle: isDark ? 'text-[#657084]' : 'text-slate-500',
  tooltip: isDark ? 'bg-[#1a1c23] text-white border-gray-600' : 'bg-white text-slate-900 border-slate-200',
  select: isDark ? 'bg-[#16181d] border-gray-700 text-white focus:ring-gray-500' : 'bg-white border-slate-300 text-slate-900 focus:ring-sky-400',
  tabActive: isDark ? 'bg-[#383b43] border-[#383b43] text-white font-bold' : 'bg-sky-100 border-sky-200 text-sky-900 font-bold',
  tabInactive: isDark ? 'bg-transparent border-[#2c2f36] text-gray-500 font-medium hover:text-gray-300 hover:border-gray-500' : 'bg-transparent border-slate-300 text-slate-500 font-medium hover:text-slate-700 hover:border-slate-400',
  chip: isDark ? 'text-white' : 'text-slate-900',
  emptyTitle: isDark ? 'text-gray-100' : 'text-slate-900',
  emptyBody: isDark ? 'text-gray-400' : 'text-slate-500',
});

const assetIcons: Record<AssetClass, React.ComponentType<{ className?: string }>> = {
  STOCK: TrendingUp,
  FUTURES: BarChart,
  CRYPTO: Bitcoin,
  FOREX: Globe,
};

const ASSET_CLASS_MAP: Record<string, AssetClass> = {
  stock: 'STOCK', stocks: 'STOCK', equity: 'STOCK', equities: 'STOCK',
  future: 'FUTURES', futures: 'FUTURES', fut: 'FUTURES',
  crypto: 'CRYPTO', cryptocurrency: 'CRYPTO', coin: 'CRYPTO', coins: 'CRYPTO',
  fx: 'FOREX', forex: 'FOREX', currency: 'FOREX',
};

const CRYPTO_ROOTS = new Set(['BTC', 'ETH', 'LTC', 'XRP', 'DOGE', 'SOL', 'ADA', 'BNB']);

const FX_CODES = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'SEK', 'NOK', 'DKK', 'HKD', 'SGD', 'CNH', 'CNY', 'MXN', 'ZAR', 'TRY', 'PLN', 'HUF', 'CZK', 'ILS', 'AED', 'SAR']);

const EQUITY_NAME_LOOKUP: Record<string, string> = {
  AAPL: 'Apple Inc.',
  AMD: 'Advanced Micro Devices, Inc.',
  AMZN: 'Amazon.com, Inc.',
  BAC: 'Bank of America Corp.',
  DIA: 'SPDR Dow Jones Industrial Average ETF',
  GOOGL: 'Alphabet Inc.',
  GOOG: 'Alphabet Inc.',
  IBM: 'IBM',
  JPM: 'JPMorgan Chase & Co.',
  META: 'Meta Platforms, Inc.',
  MSFT: 'Microsoft Corp.',
  NVDA: 'NVIDIA Corporation',
  NFLX: 'Netflix, Inc.',
  QQQ: 'Invesco QQQ Trust',
  SPY: 'SPDR S&P 500 ETF Trust',
  TSLA: 'Tesla, Inc.',
};

// Widget supports live/latest-price for STK and CRYPTO only for now.
// Unsupported long-term tagged assets (FUT, FOREX, CASH, etc.) are excluded from this widget.
const SUPPORTED_WIDGET_SEC_TYPES = new Set(['STK', 'CRYPTO']);

const isAssetSupportedForWidget = (asset: { assetClass?: string } | null | undefined) => {
  if (!asset) return false;
  try {
    const sec = getSecTypeForAssetClass((asset as any).assetClass as AssetClass);
    return SUPPORTED_WIDGET_SEC_TYPES.has(sec);
  } catch {
    return false;
  }
};

const getWidgetApiBase = () => {
  let envBase = '';
  try {
    envBase = import.meta.env?.VITE_SERVER_URL || import.meta.env?.VITE_API_BASE || '';
  } catch {}

  const clean = String(envBase || '').trim().replace(/\/+$/, '');
  if (clean) return clean;

  try {
    if (import.meta.env?.DEV) return 'http://localhost:8787';
  } catch {}

  return '';
};

const formatLiveAsOf = (value: unknown) => {
  const cutoffMs = Date.UTC(2000, 0, 1);
  if (value == null || value === '') return '';

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) && value.getTime() >= cutoffMs ? value.toLocaleString() : new Date().toLocaleString();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isFinite(date.getTime()) && date.getTime() >= cutoffMs ? date.toLocaleString() : new Date().toLocaleString();
  }

  const text = String(value).trim();
  if (!text) return '';

  if (/^\d+$/.test(text)) {
    const numeric = Number(text);
    if (Number.isFinite(numeric)) {
      const ms = numeric < 1e12 ? numeric * 1000 : numeric;
      const date = new Date(ms);
      return Number.isFinite(date.getTime()) && date.getTime() >= cutoffMs ? date.toLocaleString() : new Date().toLocaleString();
    }
  }

  const parsed = new Date(text);
  return Number.isFinite(parsed.getTime()) && parsed.getTime() >= cutoffMs ? parsed.toLocaleString() : new Date().toLocaleString();
};

// --- HELPERS ---

const PROVIDER_LABELS: Record<string, string> = {
  twelve_data: 'Twelve Data',
  tiingo: 'Tiingo',
  ibkr: 'IBKR',
};

const providerLabel = (value?: string | null): string | null => {
  const key = String(value || '').trim();
  if (!key) return null;
  return PROVIDER_LABELS[key] || key;
};

const formatCurrency = (val: number, fractionDigits?: number) => {
  const isNegative = val < 0;
  const num = Math.abs(val);
  const decimals = fractionDigits ?? (num < 10 && num > 0 ? 4 : 2);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
  return isNegative ? `-${formatted}` : formatted;
};

const formatFullDate = (d: Date, timeZone = 'UTC') => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone, weekday: 'short', month: 'short', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d).map((part) => [part.type, part.value]));
  const dayName = parts.weekday;
  const monthName = parts.month;
  const day = parts.day;
  const year = parts.year;
  let h = Number(parts.hour);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const mins = parts.minute;
  return `${dayName}, ${monthName} ${day}, ${year} - ${h.toString().padStart(2, '0')}:${mins} ${ampm}`;
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** UTC-based daily-bar date key (bars are provider-dated; never browser-shifted). */
const barUtcDateKey = (tsSec: number): string => {
  const d = new Date(Number(tsSec) * 1000);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
};

/** UTC-based, timezone-stable "Aug 4, 2026" date for a bar timestamp. */
const formatUtcBarDate = (tsSec: number): string | null => {
  const d = new Date(Number(tsSec) * 1000);
  if (!Number.isFinite(d.getTime())) return null;
  return `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
};

/**
 * Execution timestamp normalization. Accepts ISO strings, epoch milliseconds,
 * epoch seconds, numeric strings, and date-only values. Returns epoch seconds.
 */
const execTimestampSeconds = (value: unknown): number | null => {
  const ms = normalizeTimestampToMs(value);
  return ms != null && Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
};

/** U.S. market date (America/New_York) of an execution timestamp. */
const execMarketDateKey = (value: unknown): string | null => {
  const raw = String(value ?? '').trim();
  // Date-only values keep their own date; never shift through midnight parsing.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const ms = normalizeTimestampToMs(value);
  return ms != null && Number.isFinite(ms) ? getNewYorkDateKey(ms) : null;
};

const computeTradePnl = (entryPrice: number, markPrice: number, shares: number, direction: Direction) => {
  const perUnit = direction === 'SHORT' ? (entryPrice - markPrice) : (markPrice - entryPrice);
  return perUnit * shares;
};

const sanitizeSymbolForQuote = (raw: unknown) => {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  const withoutExchange = s.includes(':') ? (s.split(':').pop() || s) : s;
  const upper = withoutExchange.toUpperCase().replace(/\s+/g, '').replace(/^\/+/, '');
  return upper.replace(/!$/g, '');
};

const getDisplayNameForSymbol = (rawSymbol: unknown) => {
  const symbol = sanitizeSymbolForQuote(rawSymbol);
  if (!symbol) return '';
  return EQUITY_NAME_LOOKUP[symbol] || '';
};

const getSecTypeForAssetClass = (assetClass: AssetClass) => {
  switch (assetClass) {
    case 'FUTURES': return 'FUT';
    case 'FOREX': return 'CASH';
    case 'CRYPTO': return 'CRYPTO';
    default: return 'STK';
  }
};

const getQuoteRequestParams = (asset: InvestmentAsset) => {
  const params = new URLSearchParams();
  const symbol = getNormalizedMarketSymbol(asset.ticker, asset.assetClass);
  if (!symbol) return params;

  params.set('symbol', symbol);
  params.set('secType', getSecTypeForAssetClass(asset.assetClass));

  if (asset.assetClass === 'FUTURES') {
    params.set('exchange', 'CME');
    params.set('localSymbol', symbol);
    params.set('currency', 'USD');
  } else if (asset.assetClass === 'FOREX') {
    params.set('exchange', 'IDEALPRO');
    const compact = symbol.replace(/[^A-Z]/g, '');
    if (compact.length === 6) {
      params.set('currency', compact.slice(3));
    }
  } else if (asset.assetClass === 'CRYPTO') {
    params.set('exchange', 'PAXOS');
    params.set('currency', 'USD');
  } else {
    params.set('exchange', 'SMART');
    params.set('currency', 'USD');
  }

  return params;
};

const getNormalizedMarketSymbol = (rawSymbol: unknown, assetClass: AssetClass) => {
  const symbol = sanitizeSymbolForQuote(rawSymbol);
  if (!symbol) return '';

  if (assetClass === 'FUTURES') {
    return symbol;
  }

  if (assetClass === 'CRYPTO') {
    const compact = symbol.replace(/[^A-Z0-9]/g, '');
    if (compact.length >= 6) {
      const base = compact.slice(0, 3);
      const quote = compact.slice(3, 6);
      if (CRYPTO_ROOTS.has(base) && FX_CODES.has(quote)) return `${base}/${quote}`;
    }
    return symbol.replace(/\./g, '/').replace(/-/g, '/');
  }

  if (assetClass === 'FOREX') {
    const compact = symbol.replace(/[^A-Z]/g, '');
    if (compact.length === 6) return `${compact.slice(0, 3)}/${compact.slice(3)}`;
  }

  return symbol;
};

const getThemeForAssetClass = (assetClass: AssetClass): ThemeKey => {
  switch (assetClass) {
    case 'FUTURES': return 'yellow';
    case 'CRYPTO': return 'purple';
    case 'FOREX': return 'emerald';
    default: return 'blue';
  }
};

const getUnitName = (assetClass: AssetClass, quantity: number) => {
  const singular = Math.abs(quantity) === 1;
  switch (assetClass) {
    case 'FUTURES': return singular ? 'contract' : 'contracts';
    case 'CRYPTO': return singular ? 'coin' : 'coins';
    case 'FOREX': return singular ? 'lot' : 'lots';
    default: return singular ? 'share' : 'shares';
  }
};

const CRYPTO_HINTS = ['btc', 'eth', 'crypto', 'coin', 'token', 'usdt', 'usdc', 'sol', 'xrp', 'ada', 'dot', 'doge', 'bnb', 'avax', 'ltc', 'link', 'uni', 'shib', 'pepe'];
const FUTURES_HINTS = ['future', 'futures', 'fut', 'cme', 'cbot', 'nymex', 'comex', 'contract', 'es', 'nq', 'ym', 'rty', 'gc', 'cl', 'si', 'ng', '6e', '6b', '6j', '6a', 'zb', 'zt', 'zf', 'zn', 'zw', 'zc'];
const FOREX_HINTS = ['forex', 'fx', 'currency', 'spot', 'pair'];

const resolveAssetClassFromHint = (value?: string): AssetClass | null => {
  if (!value) return null;
  const lowered = value.toLowerCase();
  if (ASSET_CLASS_MAP[lowered]) return ASSET_CLASS_MAP[lowered];
  if (lowered.includes('crypto') || CRYPTO_HINTS.some((hint) => lowered.includes(hint))) return 'CRYPTO';
  if (lowered.includes('forex') || lowered.includes('/') || FOREX_HINTS.some((hint) => lowered.includes(hint))) return 'FOREX';
  if (lowered.includes('fut') || FUTURES_HINTS.some((hint) => lowered.includes(hint))) return 'FUTURES';
  return null;
};

const looksLikeFuturesSymbol = (value: string) => /^[A-Z]{1,5}[FGHJKMNQUVXZ]\d{1,2}$/.test(value) || /^\d?[ESMNQMRTYGCZLFHJVX]{1,5}\d{0,2}$/.test(value);

const CURRENCY_CODES = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'NZD', 'NOK', 'SEK', 'SGD', 'HKD', 'MXN', 'ZAR', 'TRY', 'PLN', 'DKK', 'HUF', 'CZK', 'RON']);

const isForexPairSymbol = (value: string) => {
  const compact = value.trim().toUpperCase().replace(/[^A-Z]/g, '');
  if (compact.length === 6 && CURRENCY_CODES.has(compact.slice(0, 3)) && CURRENCY_CODES.has(compact.slice(3))) return true;
  const pairMatch = value.trim().toUpperCase().match(/^([A-Z]{3})[./-]?([A-Z]{3})$/);
  return Boolean(pairMatch && CURRENCY_CODES.has(pairMatch[1]) && CURRENCY_CODES.has(pairMatch[2]));
};

const resolveAssetClassFromSymbol = (value?: string): AssetClass | null => {
  const symbol = String(value ?? '').trim().toUpperCase();
  if (!symbol) return null;
  if (isForexPairSymbol(symbol) || symbol.includes('/')) return 'FOREX';
  if (CRYPTO_HINTS.some((hint) => symbol.toLowerCase().includes(hint))) return 'CRYPTO';
  if (looksLikeFuturesSymbol(symbol)) return 'FUTURES';
  return null;
};

const resolveAssetClass = (record: any): AssetClass => {
  const symbolHints = [record?.symbol, record?.ticker, record?.instrument, record?.name, record?.description];
  for (const hint of symbolHints) {
    const resolved = resolveAssetClassFromSymbol(String(hint ?? '').trim());
    if (resolved) return resolved;
  }

  const hints = [
    record?.assetClass,
    record?.assetType,
    record?.securityType,
    record?.instrumentType,
    record?.marketType,
    record?.market,
    record?.category,
    record?.exchange,
    record?.symbol,
    record?.ticker,
    record?.instrument,
    record?.name,
  ];

  for (const hint of hints) {
    const resolved = resolveAssetClassFromHint(String(hint ?? '').trim());
    if (resolved) return resolved;
  }

  const baseCurrency = String(record?.baseCurrency ?? record?.base ?? record?.fromCurrency ?? '').trim().toUpperCase();
  const quoteCurrency = String(record?.quoteCurrency ?? record?.quote ?? record?.toCurrency ?? '').trim().toUpperCase();
  if (CURRENCY_CODES.has(baseCurrency) && CURRENCY_CODES.has(quoteCurrency)) return 'FOREX';

  const explicit = resolveAssetClassFromHint(String(record?.assetClass ?? record?.assetType ?? record?.securityType ?? '').trim());
  if (explicit && explicit !== 'STOCK') return explicit;

  return 'STOCK';
};

const toDirection = (value?: string, rawQty?: number): Direction => {
  const v = String(value ?? '').trim().toLowerCase();
  if (v) {
    if (v.startsWith('short') || v === 's' || v === 'sell' || v === 'sold' || v.includes('sell')) return 'SHORT';
    if (v.startsWith('long') || v === 'b' || v === 'buy' || v === 'bought' || v.includes('buy')) return 'LONG';
  }
  // fallback to quantity sign if available
  if (typeof rawQty === 'number' && Number.isFinite(rawQty)) {
    if (rawQty < 0) return 'SHORT';
    if (rawQty > 0) return 'LONG';
  }
  return 'LONG';
};

const normalizeTag = (value: unknown) => String(value ?? '').toLowerCase().replace(/[\s_-]+/g, '');

const extractTags = (record: { tags?: unknown; tag?: unknown }) => {
  if (Array.isArray(record.tags)) return record.tags.map((tag) => String(tag)).filter(Boolean);
  if (typeof record.tags === 'string') return String(record.tags).split(/[;,|]/).map((tag) => tag.trim()).filter(Boolean);
  if (typeof record.tag === 'string' && record.tag.trim()) return [record.tag.trim()];
  return [] as string[];
};

const hasLongTermTag = (record: { tags?: unknown; tag?: unknown }) => extractTags(record).some((tag) => normalizeTag(tag) === 'longterm');

// --- COMPONENTS ---

const AssetCard = ({ data, isDark = true }: { data: InvestmentAsset; isDark?: boolean }) => {
  const displayTimeZone = useDisplayTimezone();
  const [timeframe, setTimeframe] = useState<TimeframeKey>('MAX');
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverEntryPoint, setHoverEntryPoint] = useState(false);
  const [hoverExitPoint, setHoverExitPoint] = useState(false);
  const ui = getWidgetUi(isDark);

  const themeFromData = (data && (data as any).theme) as ThemeKey | undefined;
  const themeKey = themeFromData && (themes as any)[themeFromData] ? themeFromData : getThemeForAssetClass(data.assetClass);
  const theme = (themes as any)[themeKey] ?? themes.blue;
  const pnlColor = data.isProfitable ? 'text-green-500' : 'text-red-500';
  const pnlBorder = data.isProfitable ? 'border-green-500/40' : 'border-red-500/40';
  const closedBadgeClass = isDark ? 'bg-gray-700/90 border border-gray-500/60 text-gray-100' : 'bg-slate-100 border border-slate-300 text-slate-700';
  const Icon = assetIcons[data.assetClass];
  const liveQuoteReady = data.isClosed || data.hasLiveQuote === true;
  const historyLoading = Boolean(data.marketHistoryLoading)
    || (data.assetClass === 'STOCK' && !data.marketHistory && !data.marketHistoryError);
  const historyStale = Boolean(data.marketHistory?.stale);
  const latestHistoryDate = data.marketHistory?.latestCompletedBarTimestamp
    ? formatUtcBarDate(data.marketHistory.latestCompletedBarTimestamp)
    : null;

  // removed unused getContrastColor helper

  const allDailyBars = data.marketHistory?.bars ?? [];
  const visibleDailyBars = useMemo(
    () => filterDailyBarsForRange(allDailyBars, timeframe),
    [allDailyBars, timeframe]
  );
  const hasRealHistory = visibleDailyBars.length > 0;
  const chartBars = hasRealHistory
    ? visibleDailyBars
    : [{ t: Math.floor(Date.now() / 1000), o: data.entryPrice, h: data.entryPrice, l: data.entryPrice, c: data.entryPrice }];
  const prices = chartBars.map((bar) => bar.c);
  // Bar labels use UTC provider dates so daily dates never shift through the
  // browser-local timezone.
  const dates = chartBars.map((bar) => {
    const d = new Date(bar.t * 1000);
    const withYear = timeframe === '3Y' || timeframe === '5Y' || timeframe === 'MAX';
    return `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}${withYear ? `, ${String(d.getUTCFullYear()).slice(2)}` : ''}`;
  });
  const fullDates = chartBars.map((bar) => {
    const d = new Date(bar.t * 1000);
    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
    return `${dayName}, ${MONTHS_SHORT[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2, '0')}, ${d.getUTCFullYear()}`;
  });
  const entryExecSec = execTimestampSeconds(data.entryTimestamp);
  const exitExecSec = data.isClosed ? execTimestampSeconds(data.exitTimestamp) : null;
  const entryMarketDateKey = execMarketDateKey(data.entryTimestamp);
  const exitMarketDateKey = data.isClosed ? execMarketDateKey(data.exitTimestamp) : null;
  const visibleDateKeys = chartBars.map((bar) => barUtcDateKey(bar.t));
  const firstVisibleDateKey = visibleDateKeys[0] ?? null;
  const lastVisibleDateKey = visibleDateKeys[visibleDateKeys.length - 1] ?? null;
  const isDateInVisibleRange = (dateKey: string | null, execSec: number | null): boolean => {
    if (!dateKey || !firstVisibleDateKey || !lastVisibleDateKey) return false;
    if (dateKey >= firstVisibleDateKey && dateKey <= lastVisibleDateKey) return true;
    // Grace: an execution within one day of the visible edges still counts.
    // This keeps date-only/UTC-midnight timestamps (whose New York date can be
    // the prior calendar day) from being wrongly hidden.
    if (execSec != null) {
      const firstT = chartBars[0]?.t ?? 0;
      const lastT = chartBars[chartBars.length - 1]?.t ?? 0;
      if (execSec >= firstT - 86_400 && execSec <= lastT + 86_400) return true;
    }
    return false;
  };
  const entryVisible = Boolean(entryExecSec && entryMarketDateKey && isDateInVisibleRange(entryMarketDateKey, entryExecSec));
  const exitVisible = Boolean(
    data.isClosed && exitExecSec && exitMarketDateKey && isDateInVisibleRange(exitMarketDateKey, exitExecSec)
  );
  const markerPrices = [
    ...(entryVisible ? [data.entryPrice] : []),
    ...(exitVisible && Number.isFinite(Number(data.exitPrice)) ? [Number(data.exitPrice)] : []),
  ];
  const visibleMin = Math.min(...prices, ...markerPrices);
  const visibleMax = Math.max(...prices, ...markerPrices);
  const visibleRange = Math.max(visibleMax - visibleMin, Math.abs(visibleMax) * 0.01, 1);
  const pricePadding = Math.max(visibleRange * 0.08, Math.abs(visibleMax) * 0.005, 0.01);
  const paddedMin = visibleMin - pricePadding;
  const paddedMax = visibleMax + pricePadding;
  const paddedRange = Math.max(paddedMax - paddedMin, 1e-9);

  const scalePrice = (price: number) => 100 - ((price - paddedMin) / paddedRange) * 100;
  const yAxisIntervals = Array.from({ length: 7 }, (_, i) => paddedMax - (paddedRange / 6) * i);

  const getEvenIndices = () => {
    const indices: number[] = [];
    const targetCount = 5;
    for (let i = 0; i < targetCount; i++) {
      const targetIdx = Math.floor((i / (targetCount - 1)) * (dates.length - 1));
      let bestIdx = targetIdx;
      const minSearchRange = 4;
      for (let offset = 0; offset <= minSearchRange; offset++) {
        for (const sign of [1, -1] as const) {
          const checkIdx = targetIdx + (offset * sign);
          if (checkIdx >= 0 && checkIdx < dates.length) {
            const label = dates[checkIdx];
            const numMatch = label.match(/\d+/);
            if (numMatch && parseInt(numMatch[0]) % 2 === 0) {
              bestIdx = checkIdx;
              offset = minSearchRange + 1;
              break;
            }
          }
        }
      }
      indices.push(bestIdx);
    }
    return indices;
  };

  const xLabelIndices = Array.from(new Set(getEvenIndices()));
  const clampLabelPosition = (value: number) => Math.min(96, Math.max(4, value));

  const svgPoints = prices.map((price, index) => {
    const x = (index / Math.max(1, prices.length - 1)) * 100;
    const y = scalePrice(price);
    return `${x},${y}`;
  });

  const linePath = `M ${svgPoints.join(' L ')}`;
  const fillPath = `${linePath} L 100,100 L 0,100 Z`;

  const nearestBarIndex = (timestamp: number | null) => {
    if (timestamp == null) return 0;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    chartBars.forEach((bar, index) => {
      const distance = Math.abs(bar.t - timestamp);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
    return nearestIndex;
  };
  const barIndexForDateKey = (dateKey: string | null, fallbackTimestamp: number | null) => {
    if (dateKey) {
      const exact = visibleDateKeys.indexOf(dateKey);
      if (exact !== -1) return exact;
    }
    return nearestBarIndex(fallbackTimestamp);
  };
  const entryBarIndex = barIndexForDateKey(entryVisible ? entryMarketDateKey : null, entryExecSec);
  const entryX = (entryBarIndex / Math.max(1, prices.length - 1)) * 100;
  const entryY = scalePrice(data.entryPrice);

  // closed dot coordinates (if trade closed and within range)
  let exitX: number | null = null;
  let exitY: number | null = null;
  if (exitVisible) {
    const exitPriceRaw = data.exitPrice;
    if (exitPriceRaw != null && Number.isFinite(Number(exitPriceRaw))) {
      const exitPrice = Number(exitPriceRaw);
      const exitBarIndex = barIndexForDateKey(exitMarketDateKey, exitExecSec);
      exitX = (exitBarIndex / Math.max(1, prices.length - 1)) * 100;
      exitY = scalePrice(exitPrice);
    }
  }

  const isLong = data.type === 'LONG';
  const entryMarkerColor = isLong ? '#10b981' : '#ef4444';
  const exitMarkerColor = isLong ? '#ef4444' : '#10b981';
  const unitLabel = data.unitName || 'shares';

  const activeX = hoverExitPoint ? (exitX ?? entryX) : (hoverEntryPoint ? entryX : (hoverIndex !== null ? (hoverIndex / Math.max(1, dates.length - 1)) * 100 : entryX));
  const activeY = hoverExitPoint ? (exitY ?? entryY) : (hoverEntryPoint ? entryY : (hoverIndex !== null ? scalePrice(prices[hoverIndex]) : entryY));
  const activeTime = hoverExitPoint && exitExecSec
    ? formatFullDate(new Date(exitExecSec * 1000), displayTimeZone)
    : (hoverEntryPoint && entryExecSec ? formatFullDate(new Date(entryExecSec * 1000), displayTimeZone) : (hoverIndex !== null ? fullDates[hoverIndex] : data.executed));
  const activePrice = hoverExitPoint ? ( (data.isClosed && typeof data.exitPrice === 'number') ? data.exitPrice : data.currentPrice ) : (hoverEntryPoint ? data.entryPrice : (hoverIndex !== null ? prices[hoverIndex] : data.entryPrice));
  const markerAriaLabel = (kind: 'Entry' | 'Exit', tsSec: number, price: number) => {
    const priceText = Number.isFinite(price) ? `price ${formatCurrency(price)}` : 'price unavailable';
    const qty = Number.isFinite(Number(data.shares)) ? `${Math.abs(Number(data.shares))} ${unitLabel}` : 'quantity unavailable';
    return `${kind} marker: ${data.ticker}, ${formatFullDate(new Date(tsSec * 1000), displayTimeZone)}, ${priceText}, ${qty}, ${data.type === 'LONG' ? 'Long' : 'Short'}`;
  };
  const isLineHigh = activeY <= 50;
  const tooltipWidthPx = 220;
  const tooltipOffsetPx = Math.round(tooltipWidthPx / 2);
  const tooltipLeft = `clamp(8px, calc(${activeX}% - ${tooltipOffsetPx}px), calc(100% - ${tooltipWidthPx + 8}px))`;

  const handleInteraction = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!hasRealHistory) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;

    // Magnetic snap: find nearest point by X coordinate and snap if within threshold.
    const count = Math.max(1, dates.length - 1);
    const pxPositions = prices.map((_, i) => (i / count) * rect.width);
    let nearestIdx = 0;
    let minDist = Infinity;
    for (let i = 0; i < pxPositions.length; i++) {
      const d = Math.abs(pxPositions[i] - x);
      if (d < minDist) { minDist = d; nearestIdx = i; }
    }

    // Threshold in pixels (tap/mouse tolerance). Use slightly larger on small screens.
    const SNAP_THRESHOLD_PX = window.innerWidth < 420 ? 18 : 12;
    const entryPixelX = (entryX / 100) * rect.width;
    const entryDist = Math.abs(entryPixelX - x);

    // compute exit pixel X if available
    const exitPixelX = (exitX !== null ? (exitX / 100) * rect.width : null);
    const exitDist = exitPixelX !== null ? Math.abs(exitPixelX - x) : Infinity;

    if (entryDist <= SNAP_THRESHOLD_PX) {
      setHoverEntryPoint(true);
      setHoverExitPoint(false);
      setHoverIndex(null);
    } else if (exitDist <= SNAP_THRESHOLD_PX) {
      // snap to exit/closed point
      setHoverExitPoint(true);
      setHoverEntryPoint(false);
      setHoverIndex(null);
    } else if (minDist <= SNAP_THRESHOLD_PX) {
      setHoverEntryPoint(false);
      setHoverExitPoint(false);
      setHoverIndex(Math.max(0, Math.min(prices.length - 1, nearestIdx)));
    } else {
      // If not within snap threshold, clear hover to avoid jittering tooltip.
      setHoverEntryPoint(false);
      setHoverExitPoint(false);
      setHoverIndex(null);
    }
  };

  const secondaryName = (() => {
    const desc = String((data as any).description ?? data.name ?? (data as any).companyName ?? (data as any).assetName ?? '').trim();
    const lookup = getDisplayNameForSymbol(data.ticker);
    const finalName = desc || lookup;
    if (!finalName) return '';
    if (finalName === String(data.ticker).trim()) return '';
    return finalName;
  })();

  const metricItems: Array<{
    l: string;
    v: number | null;
    p?: boolean;
    pct?: boolean;
    decimals?: number;
    textClass?: string;
    loading?: boolean;
    failed?: boolean;
    accentBorder?: boolean;
    neutralBorder?: boolean;
  }> = data.isClosed
    ? [
        { l: 'Exit Price', v: (typeof data.exitPrice === 'number' && Number.isFinite(data.exitPrice) ? data.exitPrice : data.currentPrice), accentBorder: true },
        { l: 'Entry Price', v: data.entryPrice, neutralBorder: true },
        {
          l: 'Current Market',
          v: data.currentMarketPrice ?? null,
          loading: Boolean(data.currentMarketPriceLoading),
          failed: Boolean(data.currentMarketPriceFailed),
          neutralBorder: true,
        },
        { l: 'Realized Gain', v: data.gain, p: true, decimals: 2 },
        { l: 'Realized Return', v: data.returnPct, p: true, pct: true },
      ]
    : [
        {
          l: 'Current Price',
          v: data.currentMarketPrice ?? null,
          loading: Boolean(data.currentMarketPriceLoading),
          failed: Boolean(data.currentMarketPriceFailed),
          accentBorder: true,
        },
        { l: 'Entry Price', v: data.entryPrice, neutralBorder: true },
        {
          l: data.assetClass === 'STOCK' ? 'Latest Close' : 'Latest Price',
          v: data.currentPrice,
          loading: Boolean(data.supportsLiveFetch) && !liveQuoteReady && !data.fetchFailed,
          failed: Boolean(data.fetchFailed),
          neutralBorder: true,
        },
        { l: 'Unrealized Gain', v: data.gain, p: true, decimals: 2 },
        { l: 'Unrealized Return', v: data.returnPct, p: true, pct: true },
      ];

  return (
    <div
      data-testid={`investment-asset-card-${data.ticker}`}
      className={`relative flex h-full min-h-0 w-full flex-col rounded-xl border ${theme.cardBorder} px-3 pt-3 pb-0 shadow-2xl transition-all duration-300 sm:px-4 sm:pb-0`}
      style={{ backgroundColor: ui.shell }}
    >

      <div className={`absolute top-0 left-1/2 -translate-x-1/2 px-4 py-1 rounded-b-2xl ${theme.bg} text-black text-[10px] sm:text-[11px] font-extrabold tracking-widest uppercase shadow-md flex items-center gap-1.5 z-20`}>
        {Icon && <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 opacity-90" />}
        {data.assetClass}
      </div>

      <div className="relative flex justify-between items-center h-10 sm:h-12 mb-2 mt-[-6px] z-10 overflow-hidden">
        <div className="flex items-center gap-2 overflow-hidden">
          <h2 className={`text-xl sm:text-2xl font-extrabold tracking-tight leading-none shrink-0 pb-0.5 ${ui.text}`}>{data.ticker}</h2>
          <span className={`text-xs sm:text-sm font-medium truncate shrink self-center ${ui.muted}`}>{secondaryName ? `- ${secondaryName}` : ''}</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <span className={`text-xs sm:text-[15px] font-medium whitespace-nowrap ${pnlColor}`}>
            {data.shares} {Math.abs(data.shares) === 1 ? data.unitName.replace(/s$/, '') : data.unitName}
          </span>
          <span className={`px-2 sm:px-3 py-0.5 rounded-md text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-white shadow-sm ${
            data.type === 'LONG' ? (isDark ? 'bg-green-900 border border-green-500' : 'bg-emerald-600 border border-emerald-500') : (isDark ? 'bg-red-900 border border-red-500' : 'bg-rose-600 border border-rose-500')
          }`}>
            {data.type}
          </span>
        </div>
      </div>

      <div className="mb-1.5 grid grid-cols-2 gap-2 sm:grid-cols-5 lg:hidden">
        {metricItems.map((m, i) => (
          <div
            key={i}
            data-testid={m.l === 'Current Price' || m.l === 'Current Market'
              ? 'current-price-metric-mobile'
              : (m.l === 'Entry Price' ? 'entry-price-metric-mobile' : (m.l === 'Exit Price' ? 'exit-price-metric-mobile' : undefined))}
            className={`border ${m.accentBorder ? '' : (m.p ? pnlBorder : ui.border)} ${isDark ? 'bg-[#16181d]' : 'bg-slate-50'} rounded p-2 flex flex-col items-center justify-center`}
            style={m.accentBorder
              ? { borderColor: theme.primary }
              : (m.neutralBorder && isDark ? { borderColor: '#3e3f41' } : undefined)}
          >
            <span className={`text-[10px] sm:text-[11px] mb-0.5 text-center whitespace-nowrap uppercase tracking-tight font-medium opacity-80 shrink-0 ${ui.muted}`}>
              <span className="inline-flex items-center gap-2">
                <span>{m.l}</span>
                {m.l.startsWith('Latest') && !data.isClosed && Boolean(data.supportsLiveFetch) && data.hasLiveQuote && (
                  (() => {
                    const q = String(data.liveQuality || '').trim().toLowerCase();
                    const mapped = q === 'end_of_day' ? 'EOD' : (q === 'delayed' ? 'DELAYED' : (q === 'previous_close' ? 'CLOSE' : (q === 'stale' ? 'STALE' : null)));
                    if (!mapped) return null;
                    return (
                      <span
                        className="text-[9px] sm:text-[10px] px-2 py-0.5 rounded-sm font-semibold"
                        style={{ backgroundColor: theme.primary, color: 'black' }}
                      >
                        {mapped}
                      </span>
                    );
                  })()
                )}
              </span>
            </span>
              <span className={`text-xs sm:text-sm font-bold ${m.p ? pnlColor : (m.textClass ?? ui.text)}`}>
              {m.loading ? 'Loading…' : (m.failed || m.v == null ? '--' : (m.pct ? (m.v > 0 ? '+' : '') + m.v + '%' : formatCurrency(m.v, m.decimals)))}
            </span>
            {m.l.startsWith('Latest') && !data.isClosed && Boolean(data.supportsLiveFetch) && data.hasLiveQuote && data.liveAsOf && (
              <span className={`text-[9px] mt-1 block ${ui.muted}`}>{formatLiveAsOf(data.liveAsOf)}</span>
            )}
          </div>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row lg:items-stretch">
        <div className="min-w-0 flex-1">
          <div className={`relative h-[410px] w-full border ${ui.borderMuted} ${isDark ? 'bg-transparent' : 'bg-white'} rounded flex shadow-inner pt-6 pb-0 overflow-visible lg:h-[370px]`}>
            <div
              data-testid="investment-history-label"
              className={`absolute right-3 top-2 z-20 pointer-events-none rounded-full border ${isDark ? 'border-white/10 bg-black/40 text-gray-300' : 'border-slate-200 bg-white/90 text-slate-600'} px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]`}
            >
              {hasRealHistory ? 'Daily market history' : (historyLoading ? 'Loading daily market history…' : 'Market history unavailable')}
            </div>
            {hasRealHistory && latestHistoryDate && (
              <div data-testid="investment-history-as-of" className={`absolute right-3 top-9 z-20 text-[9px] font-medium uppercase tracking-wide ${historyStale ? (isDark ? 'text-amber-300' : 'text-amber-600') : ui.muted}`}>
                {historyStale ? `Stale daily history · Last available ${latestHistoryDate}` : `Updated daily · Raw prices · As of ${latestHistoryDate}`}
                {data.marketHistory?.provider ? <span data-testid="investment-provider-label"> · Market data: {providerLabel(data.marketHistory.provider)}{data.marketHistory.fallbackUsed ? ' (fallback)' : ''}</span> : null}
              </div>
            )}
            <div className={`w-16 sm:w-20 h-[calc(100%-40px)] border-r ${ui.borderMuted} flex flex-col justify-between bg-transparent z-10 relative`}>
              {hasRealHistory && yAxisIntervals.map((val, i) => (
                <div key={i} className="flex items-center justify-end gap-1 px-1 h-[0px]">
                  <span className={`text-[9px] sm:text-[10px] text-right leading-none truncate whitespace-nowrap pr-1 font-mono ${ui.muted}`}>
                    {formatCurrency(val)}
                  </span>
                  <div className={`w-1.5 h-[1px] shrink-0 ${isDark ? 'bg-gray-600/80' : 'bg-slate-300'}`}></div>
                </div>
              ))}
            </div>

            <div className="flex-1 relative flex flex-col overflow-visible">
              <div
                className="h-[calc(100%-40px)] relative cursor-crosshair touch-none"
                onMouseMove={handleInteraction}
                onMouseLeave={() => { setHoverIndex(null); setHoverEntryPoint(false); setHoverExitPoint(false); }}
                onTouchStart={handleInteraction}
                onTouchMove={handleInteraction}
                onTouchEnd={() => { setHoverIndex(null); setHoverEntryPoint(false); setHoverExitPoint(false); }}
              >
                {hasRealHistory && <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-40">
                  {yAxisIntervals.map((_, i) => <div key={i} className={`w-full h-[1px] ${isDark ? 'bg-gray-800' : 'bg-slate-200'}`}></div>)}
                </div>}
                {hasRealHistory && <div className="absolute inset-0 flex justify-between pointer-events-none opacity-10">
                  {xLabelIndices.map(idx => (
                    <div key={idx} className={`w-[1px] h-full ${isDark ? 'bg-gray-400' : 'bg-slate-300'}`} style={{ position: 'absolute', left: `${clampLabelPosition((idx / Math.max(1, dates.length - 1)) * 100)}%` }}></div>
                  ))}
                </div>}
                {hasRealHistory && <div className="absolute inset-0 pointer-events-none">
                  <div className="relative w-full h-full">
                    <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                      <defs>
                        <linearGradient id={`${data.ticker}-grad`} x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor={theme.primary} stopOpacity="0.4"/>
                          <stop offset="100%" stopColor={theme.primary} stopOpacity="0.0"/>
                        </linearGradient>
                      </defs>
                      <path d={fillPath} fill={`url(#${data.ticker}-grad)`} />
                      <path data-testid="investment-history-line" d={linePath} fill="none" stroke={theme.primary} strokeWidth="1.5" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                    </svg>
                    {entryVisible && entryExecSec != null && (
                      <div
                        data-testid="investment-entry-marker"
                        role="img"
                        aria-label={markerAriaLabel('Entry', entryExecSec, data.entryPrice)}
                        tabIndex={0}
                        className="pointer-events-auto absolute z-20 flex cursor-pointer items-center gap-1 rounded-full px-0.5"
                        style={{ left: `${entryX}%`, top: `${entryY}%`, transform: 'translate(-50%, -50%)' }}
                        onMouseEnter={() => { setHoverEntryPoint(true); setHoverExitPoint(false); setHoverIndex(null); }}
                        onMouseLeave={() => setHoverEntryPoint(false)}
                        onFocus={() => { setHoverEntryPoint(true); setHoverExitPoint(false); setHoverIndex(null); }}
                        onBlur={() => setHoverEntryPoint(false)}
                        onClick={() => { setHoverEntryPoint((v) => !v); setHoverExitPoint(false); setHoverIndex(null); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setHoverEntryPoint((v) => !v);
                            setHoverExitPoint(false);
                            setHoverIndex(null);
                          }
                        }}
                      >
                        <span className="relative flex h-3 w-3 items-center justify-center">
                          <span className="absolute inset-0 rounded-full" style={{ backgroundColor: entryMarkerColor, opacity: 0.25 }} />
                          <span className="absolute inset-0 rounded-full bg-white" style={{ boxShadow: `0 0 0 2px ${entryMarkerColor}` }} />
                          <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: entryMarkerColor }} />
                        </span>
                        <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: entryMarkerColor }}>Entry</span>
                      </div>
                    )}
                    {exitVisible && exitX !== null && exitY !== null && exitExecSec != null && typeof data.exitPrice === 'number' && (
                      <div
                        data-testid="investment-exit-marker"
                        role="img"
                        aria-label={markerAriaLabel('Exit', exitExecSec, data.exitPrice)}
                        tabIndex={0}
                        className="pointer-events-auto absolute z-20 flex cursor-pointer items-center gap-1 rounded-full px-0.5"
                        style={{ left: `${exitX}%`, top: `${exitY}%`, transform: 'translate(-50%, -50%)' }}
                        onMouseEnter={() => { setHoverExitPoint(true); setHoverEntryPoint(false); setHoverIndex(null); }}
                        onMouseLeave={() => setHoverExitPoint(false)}
                        onFocus={() => { setHoverExitPoint(true); setHoverEntryPoint(false); setHoverIndex(null); }}
                        onBlur={() => setHoverExitPoint(false)}
                        onClick={() => { setHoverExitPoint((v) => !v); setHoverEntryPoint(false); setHoverIndex(null); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setHoverExitPoint((v) => !v);
                            setHoverEntryPoint(false);
                            setHoverIndex(null);
                          }
                        }}
                      >
                        <span className="relative flex h-3 w-3 items-center justify-center">
                          <span className="absolute inset-0 rounded-full" style={{ backgroundColor: exitMarkerColor, opacity: 0.25 }} />
                          <span className="absolute inset-0 rounded-full bg-white" style={{ boxShadow: `0 0 0 2px ${exitMarkerColor}` }} />
                          <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: exitMarkerColor }} />
                        </span>
                        <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: exitMarkerColor }}>Exit</span>
                      </div>
                    )}
                  </div>
                </div>}

                {hasRealHistory && <div className="absolute inset-0 pointer-events-none z-30">
                  <div className={`absolute top-0 bottom-0 w-px border-l border-dashed ${isDark ? 'border-gray-400/50' : 'border-slate-400/50'} transition-all duration-75`} style={{ left: `${activeX}%` }} />
                  <div
                    data-testid="investment-history-tooltip"
                    className={`absolute ${ui.tooltip} text-[10px] sm:text-[11px] font-semibold px-2 sm:px-3 py-1.5 sm:py-2 rounded-md shadow-xl border flex flex-col items-start gap-0.5 transition-all duration-75`}
                    style={{
                      left: tooltipLeft,
                      top: isLineHigh ? 'auto' : '56px',
                      bottom: isLineHigh ? '12px' : 'auto',
                      width: `${tooltipWidthPx}px`,
                      minWidth: `${tooltipWidthPx}px`,
                      maxWidth: 'calc(100% - 16px)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {(hoverEntryPoint || hoverExitPoint) ? (
                      <>
                        <span className={`text-[9px] sm:text-[10px] font-bold tracking-wide truncate w-full ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>
                          {hoverExitPoint ? 'Exit' : 'Entry'} · {data.type === 'LONG' ? 'Long' : 'Short'} · {Math.abs(data.shares)} {unitLabel} · {data.ticker}
                        </span>
                        <span className={`text-[9px] sm:text-[10px] font-medium tracking-wide truncate w-full ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>{activeTime}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className={`${ui.muted} font-normal`}>{hoverExitPoint ? 'Exit Price:' : 'Entry Price:'}</span>
                          <span className={`text-[11px] sm:text-[12px] font-bold ${ui.text}`}>{formatCurrency(activePrice)}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className={`text-[9px] sm:text-[10px] font-medium tracking-wide truncate w-full ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>{activeTime}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className={`${ui.muted} font-normal`}>{hoverIndex === null ? 'Entry Price:' : 'Price:'}</span>
                          <span className={`text-[11px] sm:text-[12px] font-bold ${ui.text}`}>{formatCurrency(activePrice)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>}
                {!hasRealHistory && (
                  <div className={`absolute inset-0 flex items-center justify-center px-6 text-center text-sm ${ui.muted}`}>
                    {historyLoading
                      ? 'Loading daily market history…'
                      : 'Market history is unavailable. Position details remain available.'}
                  </div>
                )}
              </div>

              <div className={`h-10 relative bg-transparent z-10 overflow-visible border-t ${ui.borderMuted}`}>
                {hasRealHistory && xLabelIndices.map((idx, labelIdx) => (
                  <div key={labelIdx} className="absolute flex flex-col items-center top-0" style={{ left: `${clampLabelPosition((idx / Math.max(1, dates.length - 1)) * 100)}%`, transform: 'translateX(-50%)' }}>
                    <div className={`w-[1px] h-1.5 mb-1 ${isDark ? 'bg-gray-600/80' : 'bg-slate-300'}`}></div>
                    <span className={`text-[9px] sm:text-[10px] whitespace-nowrap uppercase font-medium ${ui.muted}`}>
                      {dates[idx]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex w-[150px] shrink-0 flex-col gap-1.5 pt-0 pb-0 justify-between h-[410px] lg:h-[370px] relative -top-0">
          {metricItems.map((m, i) => (
            <div
              key={i}
              data-testid={m.l === 'Current Price' || m.l === 'Current Market'
                ? 'current-price-metric-desktop'
                : (m.l === 'Entry Price' ? 'entry-price-metric-desktop' : (m.l === 'Exit Price' ? 'exit-price-metric-desktop' : undefined))}
              className={`min-h-[62px] border ${m.accentBorder ? '' : (m.p ? pnlBorder : ui.border)} ${isDark ? 'bg-[#16181d]' : 'bg-slate-50'} rounded p-1.5 flex flex-col items-center justify-center text-center`}
              style={m.accentBorder
                ? { borderColor: theme.primary }
                : (m.neutralBorder && isDark ? { borderColor: '#3e3f41' } : undefined)}
            >
              <span className={`text-[10px] mb-0.5 whitespace-nowrap uppercase tracking-tight font-medium opacity-80 shrink-0 ${ui.muted}`}>
                <span className="inline-flex items-center gap-1.5 justify-center flex-wrap">
                  <span>{m.l}</span>
                  {m.l.startsWith('Latest') && !data.isClosed && Boolean(data.supportsLiveFetch) && data.hasLiveQuote && (
                    (() => {
                      const q = String(data.liveQuality || '').trim().toLowerCase();
                      const mapped = q === 'end_of_day' ? 'EOD' : (q === 'delayed' ? 'DELAYED' : (q === 'previous_close' ? 'CLOSE' : (q === 'stale' ? 'STALE' : null)));
                      if (!mapped) return null;
                      return (
                        <span
                          className="text-[9px] px-2 py-0.5 rounded-sm font-semibold"
                          style={{ backgroundColor: theme.primary, color: 'black' }}
                        >
                          {mapped}
                        </span>
                      );
                    })()
                  )}
                </span>
              </span>
              <span className={`text-sm font-bold ${m.p ? pnlColor : (m.textClass ?? ui.text)}`}>
                {m.loading ? 'Loading…' : (m.failed || m.v == null ? '--' : (m.pct ? (m.v > 0 ? '+' : '') + m.v + '%' : formatCurrency(m.v, m.decimals)))}
              </span>
              {m.l.startsWith('Latest') && !data.isClosed && Boolean(data.supportsLiveFetch) && data.hasLiveQuote && data.liveAsOf && (
                <span className={`text-[9px] mt-1 block ${ui.muted}`}>{formatLiveAsOf(data.liveAsOf)}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="absolute left-0 right-0 bottom-3 z-30 flex flex-col sm:flex-row gap-2 sm:gap-0 justify-between items-center px-3 sm:px-4">
        <div className={`px-3 py-1 rounded-md text-[10px] sm:text-[11px] font-bold tracking-wide ${data.isClosed ? closedBadgeClass : `${ui.text} ${data.isProfitable ? (isDark ? 'bg-green-700/80' : 'bg-emerald-600/80') : (isDark ? 'bg-red-600/80' : 'bg-rose-600/80')}`}`}>
          {data.isClosed ? 'CLOSED' : 'OPEN POSITION'}
        </div>

        <div className="relative w-full sm:w-auto flex justify-center">
          <div className="hidden sm:flex space-x-1">
            {TIMEFRAMES.map(tf => (
              <button
                key={tf}
                data-testid={`investment-range-${data.ticker}-${tf}`}
                type="button"
                onClick={() => setTimeframe(tf)}
                aria-pressed={timeframe === tf}
                className={`px-2 py-0.5 flex items-center justify-center text-[10px] rounded-md border transition-colors ${timeframe === tf ? ui.tabActive : ui.tabInactive}`}
              >
                {tf}
              </button>
            ))}
          </div>
          <div className="sm:hidden w-full max-w-[1400px] relative">
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value as TimeframeKey)}
              className={`appearance-none w-full rounded-lg px-4 py-1.5 text-[10px] font-bold focus:outline-none focus:ring-1 cursor-pointer text-center ${ui.select}`}
            >
              {TIMEFRAMES.map(tf => (
                <option key={tf} value={tf}>{tf}</option>
              ))}
            </select>
            <div className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${ui.muted}`}>
              <ChevronDown className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InsightsPanel = ({ asset, isDark = true, quantityStatLabel = 'Quantity', displayTimeZone }: { asset: InvestmentAsset; isDark?: boolean; quantityStatLabel?: string; displayTimeZone: string }) => {
  const sideLabel = asset.type === 'SHORT' ? 'Short' : 'Long';

  const parseExecutedDate = (s?: string): Date | null => {
    if (!s) return null;
    // expected format: 'Tue, Feb 03, 2026 - 09:30 AM' produced by formatFullDate
    const parts = s.split(' - ');
    const datePart = parts[0] ?? s;
    const timePart = parts[1] ?? '';
    const dp = datePart.includes(',') ? datePart.split(', ').slice(1).join(', ') : datePart;
    const combined = `${dp} ${timePart}`.trim();
    const d = new Date(combined);
    return Number.isFinite(d.getTime()) ? d : null;
  };

  const entryDate = parseExecutedDate(asset.executed) ?? null;

  const exitRaw = (asset as any).exitTimestamp ?? (asset as any).exitTime ?? (asset as any).closedAt ?? null;
  let exitDate: Date | null = null;
  if (exitRaw != null) {
    const ms = normalizeTimestampToMs(exitRaw);
    if (ms != null) exitDate = new Date(ms);
  }

  const now = new Date();
  const endDate = exitDate ?? now;

  const holdingMs = entryDate ? Math.max(0, endDate.getTime() - entryDate.getTime()) : 0;
  const holdingDays = Math.floor(holdingMs / 86400000);
  const holdingHours = Math.floor((holdingMs % 86400000) / 3600000);
  const holdingLabel = entryDate ? `${holdingDays}d ${holdingHours}h` : '--';

  const quantityLabel = Number.isFinite(Number(asset.shares)) ? String(asset.shares) : '--';

  const entryDateLabel = entryDate ? entryDate.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric', timeZone: displayTimeZone }) : asset.executed ?? '--';

  const daysInTradeLabel = entryDate ? String(holdingDays) : '--';

  // Dist from entry: compute only when a valid latest price exists. Otherwise show a non-blocking placeholder.
  let distLabel = '--';
  const hasLatest = asset.isClosed
    ? Number.isFinite(Number(asset.currentPrice))
    : Boolean(asset.hasLiveQuote && Number.isFinite(Number(asset.liveQuotePrice ?? asset.currentPrice)));

  if (hasLatest && Number.isFinite(Number(asset.entryPrice)) && Number(asset.entryPrice) !== 0) {
    const latest = Number(asset.currentPrice);
    const entry = Number(asset.entryPrice);
    const pct = asset.type === 'SHORT' ? ((entry - latest) / entry) * 100 : ((latest - entry) / entry) * 100;
    distLabel = `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`;
  } else if (asset.supportsLiveFetch) {
    // supports live quotes but none available yet -> indicate loading
    distLabel = 'Loading...';
  } else {
    distLabel = '--';
  }
  const distClass = distLabel === '--'
    ? 'text-[#7d8796] text-[18px] font-bold'
    : (distLabel === 'Loading...'
      ? 'text-[#657084] text-[18px] font-medium'
      : (distLabel.startsWith('-')
        ? 'text-[#ef4444] text-[18px] font-bold'
        : 'text-[#2bd18b] text-[18px] xl:text-[19px] font-bold tracking-wide leading-tight'));

  const insightCards: Array<{
    label: string;
    value: string;
    icon: React.ComponentType<{ className?: string }>;
    valueClass?: string;
    iconClass?: string;
  }> = [
    { label: 'Entry Date', value: entryDateLabel, icon: CalendarDays, iconClass: 'text-zinc-400' },
    { label: 'Holding Period', value: holdingLabel, icon: Clock3, iconClass: 'text-zinc-400' },
    {
      label: 'Side',
      value: sideLabel,
      icon: sideLabel === 'Short' ? TrendingDown : TrendingUp,
      valueClass: sideLabel === 'Short' ? 'text-[#ef4444] text-[20px] xl:text-[21px] font-bold tracking-wide leading-none' : 'text-[#2bd18b] text-[20px] xl:text-[21px] font-bold tracking-wide leading-none',
      iconClass: sideLabel === 'Short' ? 'text-[#ef4444]' : 'text-[#2bd18b]',
    },
    { label: quantityStatLabel, value: quantityLabel, icon: Hash, iconClass: 'text-zinc-400' },
    { label: 'Days in Trade', value: daysInTradeLabel, icon: Clock3, iconClass: 'text-zinc-400' },
    {
      label: 'Dist. from Entry',
      value: distLabel,
      icon: distLabel.startsWith('-') ? ArrowDownRight : ArrowUpRight,
      valueClass: distClass,
      iconClass: distLabel === '--' || distLabel === 'Loading...' ? 'text-zinc-400' : (distLabel.startsWith('-') ? 'text-[#ef4444]' : 'text-[#2bd18b]'),
    },
  ];


  return (
    <div className="w-full h-full min-h-0 font-sans antialiased">
      <div className={`w-full h-full min-h-0 flex flex-col rounded-xl p-3 overflow-hidden border ${isDark ? 'bg-[#14151a] border-[#22242b]' : 'bg-white border-slate-200'}`}>
        <div className={`flex items-center justify-between gap-3 mb-2.5 shrink-0 ${isDark ? 'text-[#848e9c]' : 'text-slate-500'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={16} className={isDark ? 'text-gray-400 shrink-0' : 'text-slate-400 shrink-0'} />
            <span className="text-[11px] font-bold tracking-[0.15em] uppercase truncate">Insights</span>
          </div>
          <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isDark ? 'text-[#657084]' : 'text-slate-500'}`}>
            Position summary
          </span>
        </div>

        <div className={`rounded-xl p-3 flex-1 min-h-0 flex flex-col overflow-hidden border ${isDark ? 'border-[#422263] bg-[#14151a]' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center gap-2 mb-3 shrink-0">
            <Gauge size={15} className={isDark ? 'text-[#e0e1e4]' : 'text-slate-600'} />
            <h2 className={`text-[13px] font-semibold tracking-wide ${isDark ? 'text-white' : 'text-slate-900'}`}>Position Stats</h2>
          </div>

          <div className="grid grid-cols-2 gap-2.5 flex-1 min-h-0 auto-rows-[minmax(82px,1fr)]">
            {insightCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className={`rounded-xl p-3 flex flex-col justify-center min-h-0 overflow-hidden border ${isDark ? 'bg-[#191a20] border-zinc-800' : 'bg-white border-slate-200'}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className={`text-[11px] font-medium uppercase tracking-[0.04em] ${isDark ? 'text-[#7d8796]' : 'text-slate-500'}`}>{card.label}</div>
                    <Icon className={`w-4 h-4 shrink-0 ${card.iconClass ?? (isDark ? 'text-zinc-400' : 'text-slate-400')}`} />
                  </div>
                  <div className={card.valueClass ?? `${isDark ? 'text-white' : 'text-slate-900'} text-[20px] xl:text-[21px] font-bold tracking-wide leading-none`}>
                    {card.value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

// --- MAIN WIDGET ---

export const LongTermInvestmentsWidget: React.FC<LongTermInvestmentsWidgetProps> = ({
  trades: tradesProp,
  page: pageProp,
  setPage: setPageProp,
  placementHeight = 1000,
  onPageCountChange,
  isDark = true
}) => {
  const displayTimeZone = useDisplayTimezone();
  const importedRecords = useImportedTradesRecords();
  const ui = getWidgetUi(isDark);
  const [assetClassVisibility] = useAssetClassVisibility();
  const isStocksOnly = assetClassVisibility.STOCKS && !assetClassVisibility.FUTURES && !assetClassVisibility.FOREX && !assetClassVisibility.CRYPTO;
  const quantityStatLabel = isStocksOnly ? 'Shares' : 'Quantity';

  const containerRef = useRef<HTMLDivElement | null>(null);
  const prewarmSignatureRef = useRef<string>('');
  const [localPage, setLocalPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(2);
  const [livePricesById, setLivePricesById] = useState<Record<string, number>>({});
  const [failedLatestById, setFailedLatestById] = useState<Record<string, boolean>>({});
  const [liveAsOfById, setLiveAsOfById] = useState<Record<string, string>>({});
  const [liveQualityById, setLiveQualityById] = useState<Record<string, string>>({});
  const [marketHistoryById, setMarketHistoryById] = useState<Record<string, InvestmentHistoryResponse>>({});
  const [marketHistoryLoadingById, setMarketHistoryLoadingById] = useState<Record<string, boolean>>({});
  const [marketHistoryErrorById, setMarketHistoryErrorById] = useState<Record<string, string>>({});

  const page = typeof pageProp === 'number' ? pageProp : localPage;
  const setPage = (value: number | ((p: number) => number)) => {
    if (setPageProp) {
      setPageProp(value);
      return;
    }
    if (typeof value === 'function') {
      setLocalPage((prev) => (value as (p: number) => number)(prev));
    } else {
      setLocalPage(value);
    }
  };

  useEffect(() => {
    const updateItemsPerPage = () => {
      setItemsPerPage(window.innerWidth < 1024 ? 1 : 2);
    };
    // Compute items per page once on mount. Remove dynamic resize handling to
    // keep stable layout and avoid reflows when the window is resized.
    updateItemsPerPage();
  }, []);

  const mapImportedTrade = (record: any, index: number): InvestmentAsset | null => {
    const rawTimestamp = record.entryTime ?? record.entryTimestamp ?? record.executedAt ?? record.timestamp ?? record.time ?? record.date ?? record.openTime ?? record.openTimestampMs;
    const timestamp = normalizeTimestampToMs(rawTimestamp);
    if (timestamp == null) return null;
    const ticker = (record.symbol || '').trim();
    if (!ticker) return null;
    const entryPrice = Number(record.entryPrice);
    if (!Number.isFinite(entryPrice)) return null;

    const assetClass = resolveAssetClass(record);
    const rawSharesVal = Number(record.shares ?? record.volume ?? 1);
    const shares = Math.abs(rawSharesVal) || 1;
    const markPrice = Number.isFinite(Number(record.exitPrice ?? record.currentPrice ?? record.markPrice ?? record.lastPrice ?? record.price))
      ? Number(record.exitPrice ?? record.currentPrice ?? record.markPrice ?? record.lastPrice ?? record.price)
      : entryPrice;
    const pnl = Number.isFinite(Number(record.pnl))
      ? Number(record.pnl)
      : computeTradePnl(entryPrice, markPrice, shares, toDirection(record.side, rawSharesVal));
    const baseNotional = Math.abs(entryPrice * shares) || 1;
    const returnPct = Number(((pnl / baseNotional) * 100).toFixed(2));
    const type = toDirection(record.side, rawSharesVal);
    const theme = getThemeForAssetClass(assetClass);
    const name = String(record.description ?? record.companyName ?? record.company ?? record.instrument ?? record.assetName ?? getDisplayNameForSymbol(ticker) ?? ticker).trim() || ticker;
    const tags = extractTags(record);
    const exitPrice = Number.isFinite(Number(record.exitPrice ?? record.closePrice ?? record.exit_price ?? record.exit)) ? Number(record.exitPrice ?? record.closePrice ?? record.exit_price ?? record.exit) : undefined;
    const rawExitTimestamp = record.exitTime ?? record.exitTimestamp ?? record.closeTimestampMs ?? record.closedAt ?? record.closed_at_utc ?? null;
    const exitTimestamp = normalizeTimestampToMs(rawExitTimestamp);
    const isClosed = String(record.status ?? '').toLowerCase() === 'closed' || exitPrice != null || exitTimestamp != null;
    const storageId = String(record.storageId || '').trim();
    const persistedPositionId = String(
      record.persistedTradeId
        ?? (storageId.startsWith('persisted-') ? storageId.slice('persisted-'.length) : '')
        ?? ''
    ).trim();

    return {
      id: persistedPositionId || storageId || `imported-${index}`,
      ticker, name, assetClass, type, shares,
      unitName: getUnitName(assetClass, shares),
      entryPrice, currentPrice: markPrice,
      exitPrice,
      gain: Number(pnl.toFixed(2)),
      returnPct,
      executed: formatFullDate(new Date(timestamp), displayTimeZone),
      entryTimestamp: timestamp,
      exitTimestamp,
      theme,
      isProfitable: pnl >= 0,
      isClosed,
      tags,
    };
  };

  const importedAssets = useMemo(
    () => importedRecords
      .map((record, index) => mapImportedTrade(record, index))
      .filter((asset) => asset && hasLongTermTag(asset) && isAssetSupportedForWidget(asset))
      .filter((asset): asset is InvestmentAsset => Boolean(asset)),
    [importedRecords]
  );

  const assets = useMemo(() => {
    // Only consider real tagged trades: props or imported records.
    // tradesProp might be raw trade records (passed from DashboardPage as authoritativeTrades),
    // or pre-mapped InvestmentAsset objects.
    const normalizedPropAssets = Array.isArray(tradesProp)
      ? tradesProp
          .map((item, index) => {
            // Check if it's already an InvestmentAsset or a raw trade.
            // InvestmentAsset has structural properties like currentPrice, entryPrice, and returnPct.
            if (item && typeof item === 'object' && 'entryPrice' in item && 'currentPrice' in item) {
              return item as InvestmentAsset;
            }
            return mapImportedTrade(item, index);
          })
          .filter((asset): asset is InvestmentAsset => Boolean(asset))
      : [];

    const propAssets = normalizedPropAssets.filter((asset) => hasLongTermTag(asset) && isAssetSupportedForWidget(asset));
    if (propAssets.length) return propAssets;
    if (importedAssets.length) return importedAssets;
    // If there are no real tagged trades, return empty array so empty state shows.
    return [] as InvestmentAsset[];
  }, [importedAssets, tradesProp]);

  const quoteRequests = useMemo(
    () => assets.filter((asset) => {
      const sec = getSecTypeForAssetClass(asset.assetClass);
      return (asset.isClosed && (sec === 'STK' || sec === 'CRYPTO'))
        || (!asset.isClosed && sec === 'CRYPTO');
    }),
    [assets]
  );

  useEffect(() => {
    const stockAssets = assets.filter((asset) => asset.assetClass === 'STOCK');
    if (!stockAssets.length) return;
    const controller = new AbortController();
    let cancelled = false;

    setMarketHistoryLoadingById((current) => {
      const next = { ...current };
      stockAssets.forEach((asset) => { next[asset.id] = true; });
      return next;
    });

    void Promise.all(stockAssets.map(async (asset) => {
      const entryTimestamp = Number(asset.entryTimestamp);
      const exitTimestamp = Number(asset.exitTimestamp);
      const entryDate = Number.isFinite(entryTimestamp)
        ? new Date(entryTimestamp).toISOString().slice(0, 10)
        : '';
      const exitDate = Number.isFinite(exitTimestamp)
        ? new Date(exitTimestamp).toISOString().slice(0, 10)
        : null;

      try {
        const history = await getInvestmentPositionHistory({
          id: asset.id,
          symbol: asset.ticker,
          assetClass: 'STK',
          entryDate,
          entryPrice: asset.entryPrice,
          exitDate,
          exitPrice: asset.exitPrice,
          quantity: asset.shares,
          isClosed: Boolean(asset.isClosed),
        }, controller.signal);
        if (cancelled) return;
        setMarketHistoryById((current) => ({ ...current, [asset.id]: history }));
        setMarketHistoryErrorById((current) => {
          const next = { ...current };
          delete next[asset.id];
          return next;
        });
      } catch (error) {
        if (cancelled || controller.signal.aborted) return;
        setMarketHistoryErrorById((current) => ({
          ...current,
          [asset.id]: error instanceof Error ? error.message : 'Market history unavailable',
        }));
      } finally {
        if (!cancelled) {
          setMarketHistoryLoadingById((current) => ({ ...current, [asset.id]: false }));
        }
      }
    }));

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [assets]);

  useEffect(() => {
    if (!quoteRequests.length) return;

    const ctrl = new AbortController();
    let cancelled = false;

    const refreshMs = 60_000;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let loadInFlight = false;


    const loadPrices = async () => {
      if (loadInFlight) return;
      loadInFlight = true;

      try {
        const settled = await Promise.all(
          quoteRequests.map(async (asset) => {
          try {
            const params = getQuoteRequestParams(asset);
            const symbol = params.get('symbol') || '';
            const secType = getSecTypeForAssetClass(asset.assetClass);

            // Only POST widgets endpoint for STK and CRYPTO
            if (symbol && (secType === 'STK' || secType === 'CRYPTO')) {
              try {
                const body = {
                  symbol,
                  secType,
                  exchange: params.get('exchange') || undefined,
                  currency: params.get('currency') || undefined,
                };
                const apiBase = getWidgetApiBase();
                const url = apiBase ? `${apiBase}/api/widgets/latest-price` : '/api/widgets/latest-price';
                const res = await fetch(url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(body),
                  signal: ctrl.signal,
                });

                if (res.ok) {
                  const json = await res.json();
                  const raw = json?.data?.price;
                  const p = Number(raw);
                  const asOf = json?.data?.asOf ? String(json.data.asOf) : null;
                  const quality = json?.data?.quality ? String(json.data.quality) : null;
                  if (Number.isFinite(p) && p > 0) {
                    if (asOf) setLiveAsOfById((s) => ({ ...s, [asset.id]: asOf }));
                    if (quality) setLiveQualityById((q) => ({ ...q, [asset.id]: quality }));
                    return [asset.id, p] as const;
                  }
                }
              } catch {
                // Keep this cycle to one request per instrument.
              }
            }

            return null;
          } catch {
            return null;
          }
          })
        );

      if (cancelled || ctrl.signal.aborted) return;

      // Build per-asset state updates so we can atomically update prices, asOf, quality, and failed flags
      const priceUpdates: Record<string, number | null> = {};
      const failedUpdates: Record<string, boolean> = {};
      const asOfRemovals: string[] = [];
      const qualityRemovals: string[] = [];

      for (let i = 0; i < quoteRequests.length; i++) {
        const asset = quoteRequests[i];
        const entry = settled[i];
        if (entry && entry[0] === asset.id) {
          // success
          priceUpdates[asset.id] = entry[1];
          failedUpdates[asset.id] = false;
        } else {
          // failure or no price
          priceUpdates[asset.id] = null;
          failedUpdates[asset.id] = true;
          // ensure we remove stale asOf/quality for failed assets
          asOfRemovals.push(asset.id);
          qualityRemovals.push(asset.id);
        }
      }

      setLivePricesById((prev) => {
        const next = { ...prev };
        for (const id of Object.keys(priceUpdates)) {
          const v = priceUpdates[id];
          if (v == null) {
            delete next[id];
          } else {
            next[id] = v;
          }
        }
        return next;
      });

      setLiveAsOfById((prev) => {
        const next = { ...prev };
        for (const id of asOfRemovals) delete next[id];
        return next;
      });

      setLiveQualityById((prev) => {
        const next = { ...prev };
        for (const id of qualityRemovals) delete next[id];
        return next;
      });

      setFailedLatestById((prev) => {
        const next = { ...prev };
        for (const id of Object.keys(failedUpdates)) {
          next[id] = Boolean(failedUpdates[id]);
        }
        return next;
      });
      } finally {
        loadInFlight = false;
      }
    };

    void loadPrices();
    intervalId = setInterval(() => {
      void loadPrices();
    }, refreshMs);

    return () => {
      cancelled = true;
      ctrl.abort();
      if (intervalId != null) clearInterval(intervalId);
    };
  }, [quoteRequests]);



  const displayAssets = useMemo(() => {
    return assets.map((asset) => {
      const marketHistory = marketHistoryById[asset.id] ?? null;
      const latestDailyBar = marketHistory?.bars?.[marketHistory.bars.length - 1] ?? null;
      const latestDailyClose = Number(latestDailyBar?.c);
      const quotePrice = livePricesById[asset.id];
      const secType = getSecTypeForAssetClass(asset.assetClass);
      const supportsLiveFetch = secType === 'STK' || secType === 'CRYPTO';
      const marketPrice = asset.assetClass === 'STOCK' && Number.isFinite(latestDailyClose)
        ? latestDailyClose
        : quotePrice;
      const currentMarketPrice = Number.isFinite(Number(quotePrice))
        ? quotePrice
        : marketPrice;
      const hasCurrentMarketPrice = Number.isFinite(Number(currentMarketPrice));
      const currentMarketPriceFailed = !hasCurrentMarketPrice && (asset.isClosed
        ? Boolean(failedLatestById[asset.id])
        : (asset.assetClass === 'STOCK'
          ? Boolean(marketHistoryErrorById[asset.id])
          : Boolean(failedLatestById[asset.id])));
      const currentMarketPriceLoading = supportsLiveFetch
        && !hasCurrentMarketPrice
        && !currentMarketPriceFailed;
      const livePrice = asset.isClosed ? (asset.exitPrice ?? null) : marketPrice;
      const currentPrice = Number.isFinite(Number(livePrice)) ? Number(livePrice) : asset.currentPrice;
      const priceForPnl = asset.isClosed ? (asset.exitPrice ?? currentPrice) : currentPrice;
      const derivedGain = computeTradePnl(asset.entryPrice, priceForPnl, asset.shares, asset.type);
      const gain = asset.isClosed && Number.isFinite(Number(asset.gain)) ? asset.gain : derivedGain;
      const derivedReturnPct = Number((((gain) / (Math.abs(asset.entryPrice * asset.shares) || 1)) * 100).toFixed(2));
      const returnPct = asset.isClosed && Number.isFinite(Number(asset.returnPct)) ? asset.returnPct : derivedReturnPct;
      const latestBarAsOf = latestDailyBar
        ? new Date(latestDailyBar.t * 1000).toISOString()
        : null;

      return {
        ...asset,
        marketHistory,
        marketHistoryLoading: Boolean(marketHistoryLoadingById[asset.id]),
        marketHistoryError: marketHistoryErrorById[asset.id] ?? null,
        currentPrice,
        currentMarketPrice: hasCurrentMarketPrice ? Number(currentMarketPrice) : null,
        currentMarketPriceLoading,
        currentMarketPriceFailed,
        liveQuotePrice: Number.isFinite(Number(livePrice)) ? Number(livePrice) : null,
        hasLiveQuote: Number.isFinite(Number(livePrice)),
        liveAsOf: latestBarAsOf ?? liveAsOfById[asset.id] ?? null,
        liveQuality: latestBarAsOf ? 'end_of_day' : liveQualityById[asset.id] ?? null,
        fetchFailed: asset.assetClass === 'STOCK'
          ? Boolean(marketHistoryErrorById[asset.id])
          : Boolean(failedLatestById[asset.id]),
        supportsLiveFetch,
        gain: Number(gain.toFixed(2)),
        returnPct,
        isProfitable: gain >= 0,
      };
    });
  }, [
    assets,
    failedLatestById,
    liveAsOfById,
    livePricesById,
    liveQualityById,
    marketHistoryById,
    marketHistoryErrorById,
    marketHistoryLoadingById,
  ]);

  const totalPages = Math.max(1, Math.ceil(assets.length / itemsPerPage));
  const safePage = Math.min(Math.max(page, 0), totalPages - 1);

  useEffect(() => {
    onPageCountChange?.(totalPages);
  }, [onPageCountChange, totalPages]);

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [page, safePage]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setPage((curr) => Math.max(0, curr - 1));
      else if (e.key === 'ArrowRight') setPage((curr) => Math.min(totalPages - 1, curr + 1));
      else if (e.key === 'Home') setPage(0);
      else if (e.key === 'End') setPage(totalPages - 1);
    };
    node.addEventListener('keydown', handleKeyDown);
    return () => node.removeEventListener('keydown', handleKeyDown);
  }, [totalPages]);

  const currentItems = displayAssets.slice(safePage * itemsPerPage, safePage * itemsPerPage + itemsPerPage);
  const isEmpty = displayAssets.length === 0;
  const isSingleItem = currentItems.length === 1;

  const visiblePrewarmItems = useMemo(() => {
    const seen = new Set<string>();
    const items = [] as Array<{ symbol: string; secType: 'STK' | 'CRYPTO'; exchange?: string; currency?: string }>;

    for (const asset of currentItems) {
      const secType = getSecTypeForAssetClass(asset.assetClass);
      if (secType !== 'CRYPTO') continue;

      const params = getQuoteRequestParams(asset);
      const symbol = String(params.get('symbol') || '').trim().toUpperCase();
      if (!symbol) continue;

      const exchange = params.get('exchange') || undefined;
      const currency = params.get('currency') || undefined;
      const dedupeKey = [symbol, secType, exchange || '', currency || ''].join(':');
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      items.push({ symbol, secType, exchange, currency });
    }

    return items;
  }, [currentItems]);

  const visiblePrewarmSignature = useMemo(() => {
    return visiblePrewarmItems
      .map((item) => [item.symbol, item.secType, item.exchange || '', item.currency || ''].join(':'))
      .join('|');
  }, [visiblePrewarmItems]);

  useEffect(() => {
    if (!visiblePrewarmItems.length) return;
    const globalWindow = window as Window & { __TT_WIDGET_PREWARM_SIGNATURES__?: Set<string> };
    const sentSignatures = globalWindow.__TT_WIDGET_PREWARM_SIGNATURES__ || (globalWindow.__TT_WIDGET_PREWARM_SIGNATURES__ = new Set<string>());
    if (prewarmSignatureRef.current === visiblePrewarmSignature) return;
    if (sentSignatures.has(visiblePrewarmSignature)) return;
    prewarmSignatureRef.current = visiblePrewarmSignature;
    sentSignatures.add(visiblePrewarmSignature);

    const apiBase = getWidgetApiBase();
    const url = apiBase ? `${apiBase}/api/widgets/latest-price/prewarm` : '/api/widgets/latest-price/prewarm';
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instruments: visiblePrewarmItems }),
    }).catch(() => {});
  }, [visiblePrewarmItems, visiblePrewarmSignature]);

  const handleEmptyClick = () => {
    try {
      const nav = (window as any).__TT_navigate;
      if (typeof nav === 'function') nav('Trades');
      else window.location.pathname = '/trades';
    } catch {}
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isEmpty) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleEmptyClick();
    }
  };

  return (
    <div
      data-testid="long-term-investments-content"
      ref={containerRef}
      tabIndex={0}
      onClick={isEmpty ? handleEmptyClick : undefined}
      onKeyDown={handleKeyPress}
      style={{ height: `${placementHeight}px` }}
      className={`flex flex-col overflow-hidden rounded-xl outline-none focus:outline-none p-1 sm:p-2 ${ui.text} ${isEmpty ? 'cursor-pointer' : ''}`}
    >
      {isEmpty ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center px-6 py-8">
            <div className={`text-sm ${ui.muted}`}>
              {((tradesProp && tradesProp.length > 0) || importedRecords.length > 0)
                ? 'Tag trades as Long Term to see this widget.'
                : 'Import trades and tag them as Long Term to see this widget.'}
            </div>
          </div>
        </div>
      ) : isSingleItem ? (
        <div className="flex h-full min-h-0 flex-col gap-2 lg:flex-row lg:gap-3 pr-2 sm:pr-3">
          <div className="min-h-0 w-full lg:basis-[62%] lg:max-w-[62%] lg:flex-none">
            <AssetCard data={currentItems[0]} isDark={isDark} />
          </div>
          <div className="min-h-0 w-full lg:basis-[38%] lg:max-w-[38%] lg:flex-none">
            <InsightsPanel asset={currentItems[0]} isDark={isDark} quantityStatLabel={quantityStatLabel} displayTimeZone={displayTimeZone} />
          </div>
        </div>
      ) : (
        <div className="grid h-full grid-cols-1 gap-2 lg:grid-cols-2 sm:gap-2.5 flex-1 pr-2 sm:pr-3">
          {currentItems.map((asset) => (
            <div key={asset.id} className="h-full min-h-0">
              <AssetCard data={asset} isDark={isDark} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- PREVIEW WRAPPER ---
// This App component is required for the preview environment to render the widget correctly.
export default function App() {
  return (
    <div className="min-h-screen bg-[#0a0c10] p-4 flex items-center justify-center">
      <div className="max-w-[1400px] w-full">
        <LongTermInvestmentsWidget placementHeight={900} />
      </div>
    </div>
  );
}
